/** @jsx jsx */
import { useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { Stack } from '@balance-web/stack';
import type { TooltipProps } from '@balance-web/tooltip';
import { makeIdFactory, useId } from '@balance-web/utils';
import { Flex } from '@balance-web/flex';
import { Inline } from '@balance-web/inline';
import { theme } from '@balance-web/theme';
import { Text } from '@balance-web/text';

import { FieldContext } from './context';
import { FieldDescription } from './FieldDescription';
import { FieldLabel } from './FieldLabel';
import { FieldMessage } from './FieldMessage';
import { HelpIndicator } from './HelpIndicator';

const FieldTestIdFactory = makeIdFactory('field', [
  'description',
  'input',
  'label',
  'message',
  'helpIndicator',
  'labelSuffix',
]);
/**
 * About
 * ------------------------------
 * This form field component consolidates the elements **around** an input with
 * a single API. The a11y requirements are too specific to consider composition
 * within each exposed field type.
 */

type FieldRenderProps = {
  inputProps: {
    'aria-describedby'?: string;
    invalid: boolean;
    id: string;
  };
  descriptionId: string;
  labelId: string;
  messageId: string;
};

export type FieldProps = WithDataAttributeProp<{
  /** Optionally describe the intent of the input where the label alone would not suffice. */
  description?: ReactElement | string;

  /** The optional element is visible inline with 'invalidMessage'.
   * For example, it can be used to display remaining characters for the input field (text-input or text-area) */
  guidance?: ReactNode;
  /**
   * Help text is hidden by default, and revealed in a tooltip from an icon
   * beside the label. Use help text to clarify anything that may be ambiguous
   * about the input. In most cases a description is preferred.
   */
  helpText?: TooltipProps['content'];
  /** Used for aria attributes and various element ID suffixes. Defaults to a generated ID. */
  id?: string;
  /** The label above the input. */
  label: string;
  /** When false, the label will only be visible to screen-readers. */
  labelVisible?: boolean;
  /** Show "(required)" or "(optional)" after the label, in regular weight. */
  requiredOrOptional?: 'required' | 'optional';
  /** Explain how the user's input doesn't meet the requirements of the field. */
  invalidMessage?: string;
  /**
   * Reserve the vertical space that will be taken by an invalid message. You
   * would use this to stop subsequent elements from moving when an invalid
   * message is displayed.
   */
  reserveMessageSpace?: boolean;
  /**
   * Decides if label and input should be laid out horizontally or vertically.
   * Default value is vertical.
   */
  layout?: 'horizontal' | 'vertical';
}>;

/** @private */
type InternalFieldProps = FieldProps & {
  /** Called with arguments derived from internal state. */
  children: ReactNode | ((fieldRenderProps: FieldRenderProps) => ReactNode);
};

// Component
// ------------------------------

export const Field = (props: InternalFieldProps) => {
  const {
    children,
    description,
    guidance,
    helpText,
    id: _id,
    invalidMessage,
    label,
    labelVisible,
    requiredOrOptional,
    reserveMessageSpace,
    layout,
  } = props;

  const _layout = layout || 'vertical';

  // generate IDs
  const id = useId(_id);
  const testIds = FieldTestIdFactory(getTestIdFromDataAttributes(props.data));
  const baseId = testIds.base(id);
  const descriptionId = testIds.description(id);
  const inputId = testIds.input(id);
  const labelId = testIds.label(id);
  const messageId = testIds.message(id);
  const helpIndicatorId = testIds.helpIndicator(id);
  const labelSuffixId = testIds.labelSuffix(id);

  const inputDescribedBy = invalidMessage
    ? messageId
    : description
    ? descriptionId
    : undefined;

  const labelContent = requiredOrOptional ? (
    <span>
      {label}{' '}
      <Text inline data={{ testid: labelSuffixId }} weight="regular">
        ({requiredOrOptional})
      </Text>
    </span>
  ) : (
    label
  );

  const labelElement = (
    <FieldLabel
      data={{ testid: labelId }}
      htmlFor={inputId}
      id={labelId}
      visible={labelVisible}
    >
      {_layout === 'horizontal' ? (
        <Inline alignY="center" height="100%">
          {labelContent}
        </Inline>
      ) : (
        labelContent
      )}
    </FieldLabel>
  );

  const context = useMemo(() => {
    return {
      'aria-describedby': inputDescribedBy,
      invalid: Boolean(invalidMessage),
      id: inputId,
      labelId,
    };
  }, [inputDescribedBy, invalidMessage, inputId, labelId]);

  const childrenElement =
    typeof children === 'function'
      ? children({
          inputProps: context,
          descriptionId,
          labelId,
          messageId,
        })
      : children;

  const fieldMessageVisible = invalidMessage || reserveMessageSpace;

  if (_layout === 'horizontal') {
    return (
      <FieldContext.Provider value={context}>
        <Flex
          alignItems="center"
          data={{ testid: baseId }}
          data-balance-component="field"
          flex="1"
          gap={labelVisible === false ? 'none' : 'small'}
          id={baseId}
          justifyContent={labelVisible === false ? 'start' : 'space-between'}
        >
          <Stack>
            {helpText ? (
              // NOTE: this div will NOT collapse (into the stack grid) when the label
              // is visually hidden. This is a pretty low-impact bug that's very
              // tricky to resolve. We mitigate the issue here by only rendering the
              // div if a help indicator is required.
              <div style={{ alignItems: 'center', display: 'flex' }}>
                {labelElement}
                <div style={{ paddingRight: theme.spacing.xsmall }}>
                  <HelpIndicator
                    content={helpText}
                    data={{ testid: helpIndicatorId }}
                  />
                </div>
              </div>
            ) : (
              labelElement
            )}
            {description ? (
              <FieldDescription
                data={{ testid: descriptionId }}
                id={descriptionId}
              >
                {description}
              </FieldDescription>
            ) : null}
          </Stack>
          <Stack flex="1">
            {childrenElement}
            {fieldMessageVisible || guidance ? (
              <Flex
                gap="small"
                justifyContent={
                  fieldMessageVisible ? 'space-between' : 'flex-end'
                }
              >
                {fieldMessageVisible ? (
                  <FieldMessage data={{ testid: messageId }} id={messageId}>
                    {invalidMessage || <div>&nbsp;</div>}
                  </FieldMessage>
                ) : null}
                {guidance}
              </Flex>
            ) : null}
          </Stack>
        </Flex>
      </FieldContext.Provider>
    );
  }

  return (
    <FieldContext.Provider value={context}>
      <Stack
        data={{ testid: baseId }}
        data-balance-component="field"
        gap="xsmall"
        id={baseId}
      >
        {helpText ? (
          // NOTE: this div will NOT collapse (into the stack grid) when the label
          // is visually hidden. This is a pretty low-impact bug that's very
          // tricky to resolve. We mitigate the issue here by only rendering the
          // div if a help indicator is required.
          <div style={{ alignItems: 'center', display: 'flex' }}>
            {labelElement}
            <HelpIndicator
              content={helpText}
              data={{ testid: helpIndicatorId }}
            />
          </div>
        ) : (
          labelElement
        )}

        {description ? (
          <FieldDescription data={{ testid: descriptionId }} id={descriptionId}>
            {description}
          </FieldDescription>
        ) : null}

        {childrenElement}
        {fieldMessageVisible || guidance ? (
          <Flex
            gap="small"
            justifyContent={fieldMessageVisible ? 'space-between' : 'flex-end'}
          >
            {fieldMessageVisible ? (
              <FieldMessage data={{ testid: messageId }} id={messageId}>
                {invalidMessage || <div>&nbsp;</div>}
              </FieldMessage>
            ) : null}
            {guidance}
          </Flex>
        ) : null}
      </Stack>
    </FieldContext.Provider>
  );
};
